import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Printer, Truck } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getDispatchBoard, saveDispatch } from "@/lib/admin/production.functions";
import { getDispatchRoster } from "@/lib/admin/delivery.functions";
import { printDispatchSheet } from "@/lib/admin/production-print";
import { DELIVERY_STATE_LABEL, istToday } from "@/lib/admin/production.schemas";
import { orderSourceLabel } from "@/lib/admin/ops-sources";

export const Route = createFileRoute("/_admin/admin/dispatch")({
  head: () => ({
    meta: [
      { title: "Dispatch — Poonam Kapur Healthy Kitchen Admin" },
      {
        name: "description",
        content: "Assign drivers and vehicles to ready orders and print delivery slips.",
      },
      { property: "og:title", content: "Dispatch — Poonam Kapur Healthy Kitchen Admin" },
      { property: "og:description", content: "Assign drivers and vehicles to ready orders and print delivery slips." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DispatchPage,
});

function DispatchPage() {
  const queryClient = useQueryClient();
  const [day, setDay] = useState(istToday());
  const [draft, setDraft] = useState<
    Record<string, { driverId: string; vehicleId: string; driver: string; vehicle: string }>
  >({});

  const listFn = useServerFn(getDispatchBoard);
  const saveFn = useServerFn(saveDispatch);
  const rosterFn = useServerFn(getDispatchRoster);

  const board = useQuery({
    queryKey: ["admin", "dispatch", day],
    queryFn: () =>
      listFn({
        data: { from: day, to: day, statuses: ["ready", "out_for_delivery"] },
      }),
    staleTime: 5_000,
    placeholderData: (prev) => prev,
  });

  const roster = useQuery({
    queryKey: ["admin", "delivery", "roster"],
    queryFn: () => rosterFn(),
    staleTime: 30_000,
  });

  const drivers = useMemo(() => roster.data?.drivers ?? [], [roster.data]);
  const vehicles = useMemo(() => roster.data?.vehicles ?? [], [roster.data]);

  const save = useMutation({
    mutationFn: (v: {
      orderId: string;
      driver?: string;
      vehicle?: string;
      driverId?: string | null;
      vehicleId?: string | null;
      state?: "out_for_delivery";
      markDispatched: boolean;
    }) => saveFn({ data: v }),
    onSuccess: () => {
      toast.success("Dispatch updated");
      void queryClient.invalidateQueries({ queryKey: ["admin", "dispatch"] });
    },
    onError: () => toast.error("Couldn't update dispatch."),
  });

  const rows = board.data ?? [];


  return (
    <div>
      <AdminPageHeader
        title="Dispatch"
        description="Orders that are ready to leave the kitchen. Assign a driver and vehicle, then dispatch."
        actions={
          <AdminDataToolbar
            exportName="dispatch"
            moduleId="dispatch"
            onImported={() =>
              void queryClient.invalidateQueries({ queryKey: ["admin", "dispatch"] })
            }
            rows={() =>
              rows.map((r) => ({
                "Order ID": r.id,
                "Order Ref": r.order_ref,
                Customer: r.customer_name,
                "Driver Name": r.driver_name,
                "Vehicle Number": r.vehicle_number,
                "Delivery State": r.delivery_state,
                "Dispatched At": r.dispatched_at ?? "",
                "Delivered At": r.delivered_at ?? "",
                Notes: r.notes,
              }))
            }
          />
        }
      />


      <RefreshBar
        targets={[{ key: "dispatch", label: "dispatch" }]}
        busy={board.isFetching}
        lastUpdated={board.dataUpdatedAt || null}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value || istToday())}
          className="h-9 w-40"
          aria-label="Dispatch date"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={rows.length === 0}
          onClick={() => printDispatchSheet(rows, day, "Dispatch sheet")}
          className="gap-1.5"
        >
          <Printer className="h-3.5 w-3.5" aria-hidden />
          Print dispatch sheet
        </Button>
        <span className="text-xs text-muted-foreground">
          Drivers and vehicles come from{" "}
          <Link to="/admin/delivery-partners" className="underline underline-offset-4">
            Delivery Partners
          </Link>{" "}
          and{" "}
          <Link to="/admin/vehicles" className="underline underline-offset-4">
            Vehicles
          </Link>
          .
        </span>
      </div>

      {board.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : board.isError ? (
        <p role="alert" className="rounded-lg border border-destructive p-6 text-sm text-destructive">
          Dispatch jobs could not be loaded. Refresh and try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          No orders are ready for dispatch on this day.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Area</th>
                <th className="px-3 py-2 text-left">Slot time</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Driver</th>
                <th className="px-3 py-2 text-left">Vehicle</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-left">Dispatch time</th>
                <th className="px-3 py-2 text-left">State</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const d =
                  draft[r.id] ??
                  {
                    driverId: r.driver_id ?? "",
                    vehicleId: r.vehicle_id ?? "",
                    driver: r.driver_name,
                    vehicle: r.vehicle_number,
                  };
                const driver = drivers.find((x) => x.id === d.driverId);
                const vehicle = vehicles.find((x) => x.id === d.vehicleId);
                const blockers = [...(driver?.blockers ?? []), ...(vehicle?.blockers ?? [])];
                const canDispatch = Boolean(driver) && blockers.length === 0;

                return (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono text-xs">{r.order_ref}</td>
                    <td className="px-3 py-2">{r.neighbourhood || "—"}</td>
                    <td className="px-3 py-2">{r.preferred_delivery_time.slice(0, 5)}</td>
                    <td className="px-3 py-2">{orderSourceLabel(r.order_channel)}</td>
                    <td className="px-3 py-2">
                      <Select
                        value={d.driverId || "none"}
                        onValueChange={(v) => {
                          const picked = drivers.find((x) => x.id === v);
                          setDraft((p) => ({
                            ...p,
                            [r.id]: {
                              driverId: v === "none" ? "" : v,
                              driver: picked?.full_name ?? "",
                              vehicleId: picked?.vehicle_id ?? d.vehicleId,
                              vehicle: picked?.vehicle_number ?? d.vehicle,
                            },
                          }));
                        }}
                      >
                        <SelectTrigger className="h-8 w-52" aria-label={`Driver for ${r.order_ref}`}>
                          <SelectValue placeholder="Select driver" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {drivers.map((x) => (
                            <SelectItem
                              key={x.id}
                              value={x.id}
                              disabled={x.blockers.length > 0}
                            >
                              {x.full_name}
                              {x.scheduled_today ? " · on shift" : ""}
                              {x.blockers.length > 0 ? ` · ${x.blockers[0]}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {driver?.zone_name ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          Zone: {driver.zone_name}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={d.vehicleId || "none"}
                        onValueChange={(v) => {
                          const picked = vehicles.find((x) => x.id === v);
                          setDraft((p) => ({
                            ...p,
                            [r.id]: {
                              ...d,
                              vehicleId: v === "none" ? "" : v,
                              vehicle: picked?.vehicle_number ?? "",
                            },
                          }));
                        }}
                      >
                        <SelectTrigger className="h-8 w-44" aria-label={`Vehicle for ${r.order_ref}`}>
                          <SelectValue placeholder="Select vehicle" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No vehicle</SelectItem>
                          {vehicles.map((x) => (
                            <SelectItem key={x.id} value={x.id} disabled={x.blockers.length > 0}>
                              {x.vehicle_number}
                              {x.blockers.length > 0 ? ` · ${x.blockers[0]}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {driver?.phone || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {r.dispatched_at
                        ? new Date(r.dispatched_at).toLocaleTimeString("en-IN")
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {DELIVERY_STATE_LABEL[r.delivery_state]}
                      {blockers.length > 0 ? (
                        <p className="mt-1 text-[11px] text-destructive">{blockers.join(" · ")}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            save.mutate({
                              orderId: r.id,
                              driver: d.driver,
                              vehicle: d.vehicle,
                              driverId: d.driverId || null,
                              vehicleId: d.vehicleId || null,
                              markDispatched: false,
                            })
                          }
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          className="gap-1.5"
                          disabled={!canDispatch}
                          title={
                            canDispatch
                              ? undefined
                              : blockers[0] ?? "Select an available driver first"
                          }
                          onClick={() =>
                            save.mutate({
                              orderId: r.id,
                              driver: d.driver,
                              vehicle: d.vehicle,
                              driverId: d.driverId || null,
                              vehicleId: d.vehicleId || null,
                              state: "out_for_delivery",
                              markDispatched: true,
                            })
                          }
                        >
                          <Truck className="h-3.5 w-3.5" aria-hidden />
                          Dispatch
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => printDispatchSheet([r], day, "Delivery slip")}
                        >
                          <Printer className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
